import { AppPageLayout } from "@/components/common/AppPageLayout";
import Spinner from "@/components/common/Spinner";
import Stepper from "@/components/common/Stepper";
import { TemplateLockProvider } from "@/features/templates/components/TemplateScopeLock";
import { hasTemplateAiDraftMarker, useTemplateAiDraftHydrated } from "@/features/ai-builder/useTemplateAiDraftStore";
import { TemplateAiAssistant } from "@/features/ai-builder/TemplateAiAssistant";
import { selectFormsLiteQueryOptions } from "@/features/forms/api/selectForms";
import { schedulesQueryOptions } from "@/features/schedules/api/selectSchedules";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateAdminView } from "@/features/templates/hooks/useTemplateAdminView";
import { TemplateAdminViewToggle } from "@/features/templates/components/TemplateAdminViewToggle";
import { templateStepperLabel, useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { hooks as h } from "@/hooks";
import { useAuth } from "@/utils/auth";
import { createFileRoute, Outlet, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

const ADMIN_STEP_PATHS = ["/screener", "/consent", "/hospital-number", "/labels"];

const NewTemplateFormLayout = () => {
    const { templateId } = Route.useParams();
    const router = useRouter();
    const navigate = useNavigate();
    const currentPath = router.state.location.pathname;
    const { steps, setAdminView: setWizardAdminView } = useTemplateNavigationStore();
    const { isAdminView } = useTemplateAdminView();

    // Keep the wizard's steps in line with the admin view toggle.
    useEffect(() => {
        setWizardAdminView(isAdminView);
    }, [isAdminView, setWizardAdminView]);

    // Turning admin view off while on an admin-only step moves the user back to the Date step.
    useEffect(() => {
        const onAdminStep = ADMIN_STEP_PATHS.some((path) => currentPath.includes(path));
        if (!isAdminView && onAdminStep) {
            void navigate({ to: "/templates/$templateId/date", params: { templateId } });
        }
    }, [isAdminView, currentPath, navigate, templateId]);
    const copy = useTemplatesCopy();
    const localizedSteps = useMemo(
        () => steps.map((step) => ({ ...step, words: templateStepperLabel(step.key, copy) })),
        [steps, copy]
    );
    const auth = useAuth();
    const hydrated = useTemplateAiDraftHydrated();
    const waitingForAiDraft = Boolean(auth?.id) && hasTemplateAiDraftMarker(templateId, auth.id) && !hydrated;

    h.useNav([
        { label: copy.templates, route: { to: "/templates" } },
        {
            label: copy.newTemplate,
            route: {
                to: Route.to,
                params: { templateId: templateId },
            },
        },
    ]);

    const shouldShowStepper = useMemo(() => {
        return !currentPath.includes("/publish");
    }, [currentPath]);

    return (
        <AppPageLayout
            width={isAdminView ? "full" : "md"}
            padding={isAdminView ? "lg" : "none"}
            as="div"
            className="space-y-6"
        >
            {shouldShowStepper && (
                <div className="space-y-3">
                    <TemplateAdminViewToggle floating />
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <Stepper steps={localizedSteps} />
                        </div>
                        <TemplateAiAssistant templateId={templateId} />
                    </div>
                </div>
            )}
            {waitingForAiDraft ? (
                <div className="flex justify-center py-16">
                    <Spinner className="h-10 w-10" />
                </div>
            ) : (
                <TemplateLockProvider templateId={templateId}>
                    <Outlet />
                </TemplateLockProvider>
            )}
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/_app/templates/$templateId")({
    component: NewTemplateFormLayout,
    loader: async ({ context }) => {
        await Promise.all([
            context.queryClient.ensureQueryData(selectFormsLiteQueryOptions()),
            context.queryClient.ensureQueryData(schedulesQueryOptions()),
        ]);
    },
});
